import { PlusIcon } from 'lucide-react'

import { Button, Loader } from '@/components/ui'
import { isTicketUnread, useSupportLastSeenAt } from '../store'
import { groupTicketsByStatus } from '../ticketSections'
import type { SupportTicket } from '../types'
import { TicketListEmptyState } from './TicketListEmptyState'
import { TicketListRow } from './TicketListRow'

interface TicketListProps {
  tickets: SupportTicket[]
  loading: boolean
  selectedPublicId: string | null
  onSelect: (publicId: string) => void
  onNewTicket: () => void
}

export const TicketList = ({
  tickets,
  loading,
  selectedPublicId,
  onSelect,
  onNewTicket,
}: TicketListProps) => {
  const lastSeenAt = useSupportLastSeenAt()
  const sections = groupTicketsByStatus(tickets)

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-blue-1">
      <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
        <h2 className="text-sm font-semibold text-blue-7">Your tickets</h2>
        <Button size="sm" onClick={onNewTicket}>
          <PlusIcon />
          New ticket
        </Button>
      </div>

      <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-2.5">
        {loading && tickets.length === 0 && (
          <div className="flex justify-center py-8">
            <Loader />
          </div>
        )}

        {!loading && tickets.length === 0 && (
          <TicketListEmptyState onNewTicket={onNewTicket} />
        )}

        {sections.map((section) => (
          <section key={section.status} className="space-y-1">
            <h3 className="flex items-center gap-1.5 px-2.5 pb-1 text-xs font-semibold uppercase tracking-wide text-blue-6">
              {section.status === 'awaiting_user' && (
                <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />
              )}
              {section.label}
              <span className="font-medium text-blue-4">
                {section.tickets.length}
              </span>
            </h3>
            {section.tickets.map((ticket) => (
              <TicketListRow
                key={ticket.publicId}
                ticket={ticket}
                selected={ticket.publicId === selectedPublicId}
                unread={isTicketUnread(ticket, lastSeenAt)}
                onSelect={onSelect}
              />
            ))}
          </section>
        ))}
      </div>
    </div>
  )
}
